/**
 * Region-of-interest editor (capability station:roi:draw).
 *
 * The backend already accepts `roi` on baseline add and station PATCH; this is
 * the missing surface. Drag to draw, drag inside to move, corner handles to
 * resize. Coordinates are stored as fractions of the frame, so the ROI stays
 * correct if the camera resolution changes.
 */
import { useRef, useState } from 'react';

export interface Roi {
  x: number;
  y: number;
  w: number;
  h: number;
}

type Mode = 'idle' | 'draw' | 'move' | 'se' | 'nw';

export function RoiEditor({
  imageSrc,
  initial,
  onSave,
  busy,
}: {
  imageSrc: string;
  initial: Roi | null;
  onSave: (roi: Roi | null) => Promise<void>;
  busy?: boolean;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [roi, setRoi] = useState<Roi | null>(initial);
  const [drawing, setDrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const drag = useRef<{ mode: Mode; startX: number; startY: number; start: Roi } | null>(null);

  const clamp = (v: number) => Math.min(1, Math.max(0, v));

  function frac(e: React.PointerEvent): { fx: number; fy: number } {
    const rect = stageRef.current!.getBoundingClientRect();
    return {
      fx: clamp((e.clientX - rect.left) / rect.width),
      fy: clamp((e.clientY - rect.top) / rect.height),
    };
  }

  function onStageDown(e: React.PointerEvent) {
    if (!drawing) return;
    e.preventDefault();
    const { fx, fy } = frac(e);
    drag.current = { mode: 'draw', startX: fx, startY: fy, start: { x: fx, y: fy, w: 0, h: 0 } };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onRectDown(e: React.PointerEvent, mode: Mode) {
    if (drawing) return;
    e.preventDefault();
    e.stopPropagation();
    const { fx, fy } = frac(e);
    drag.current = { mode, startX: fx, startY: fy, start: roi! };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }

  function onMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const { fx, fy } = frac(e);
    const dx = fx - d.startX;
    const dy = fy - d.startY;
    if (d.mode === 'draw') {
      setRoi({
        x: Math.min(d.startX, fx),
        y: Math.min(d.startY, fy),
        w: Math.abs(dx),
        h: Math.abs(dy),
      });
    } else if (d.mode === 'move') {
      setRoi({
        ...d.start,
        x: clamp(d.start.x + dx),
        y: clamp(d.start.y + dy),
      });
    } else if (d.mode === 'se') {
      setRoi({
        ...d.start,
        w: clamp(d.start.w + dx),
        h: clamp(d.start.h + dy),
      });
    } else if (d.mode === 'nw') {
      const nx = clamp(d.start.x + dx);
      const ny = clamp(d.start.y + dy);
      setRoi({
        x: nx,
        y: ny,
        w: clamp(d.start.w + (d.start.x - nx)),
        h: clamp(d.start.h + (d.start.y - ny)),
      });
    }
  }

  function onUp() {
    const d = drag.current;
    drag.current = null;
    if (d?.mode === 'draw') {
      setDrawing(false);
      setRoi((r) => (r && r.w > 0.02 && r.h > 0.02 ? r : null));
    }
  }

  return (
    <div>
      <div
        className="roi-stage"
        ref={stageRef}
        onPointerDown={onStageDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        style={{ cursor: drawing ? 'crosshair' : 'default' }}
      >
        <img src={imageSrc} alt="Latest capture — inspection region editor" draggable={false} />
        {roi && (
          <div
            className="roi-rect"
            style={{
              left: `${roi.x * 100}%`,
              top: `${roi.y * 100}%`,
              width: `${roi.w * 100}%`,
              height: `${roi.h * 100}%`,
            }}
            onPointerDown={(e) => onRectDown(e, 'move')}
          >
            <span className="roi-label">INSPECTION REGION</span>
            <span className="h nw" onPointerDown={(e) => onRectDown(e, 'nw')} />
            <span className="h se" onPointerDown={(e) => onRectDown(e, 'se')} />
          </div>
        )}
      </div>

      <div className="btn-row" style={{ marginTop: 10 }}>
        {!drawing ? (
          <button className="btn sm" onClick={() => setDrawing(true)}>
            {roi ? 'Redraw region' : 'Draw region'}
          </button>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Drag on the image to define the region…
          </span>
        )}
        <button
          className="btn sm"
          disabled={!roi || saving || busy}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(roi);
            } finally {
              setSaving(false);
            }
          }}
        >
          Save region
        </button>
        <button
          className="btn sm ghost"
          disabled={!roi || saving || busy}
          onClick={async () => {
            setRoi(null);
            setSaving(true);
            try {
              await onSave(null);
            } finally {
              setSaving(false);
            }
          }}
        >
          Reset
        </button>
        {roi && (
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            x {(roi.x * 100).toFixed(0)}% · y {(roi.y * 100).toFixed(0)}% · w {(roi.w * 100).toFixed(0)}% · h{' '}
            {(roi.h * 100).toFixed(0)}%
          </span>
        )}
      </div>
      <div className="card-note" style={{ marginTop: 6 }}>
        The region is stored as fractions of the frame and applied to baseline
        learning and every inspection cycle on this station.
      </div>
    </div>
  );
}
